import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/Layout';
import { Icon } from '@/components/kol/Icon';
import {
  ApiError,
  listGrantsForVehicle,
  revokeGrant,
  sendInvite,
  type GrantRow,
  type PermissionScope,
} from '@/lib/api';

const GRANTABLE_SCOPES: Exclude<PermissionScope, 'OWNER'>[] = ['FULL', 'DRIVE', 'READ', 'VIEW'];

export function PermissionsPage() {
  const { t } = useTranslation();
  const { id: vehicleId } = useParams<{ id: string }>();
  const [grants, setGrants] = useState<GrantRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!vehicleId) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await listGrantsForVehicle(vehicleId);
        if (!cancelled) setGrants(data.items);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t('common.error'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleId, refresh, t]);

  if (!vehicleId) return null;

  return (
    <Layout
      title={t('vehicle.permissions_title')}
      trailing={
        <Link
          to={`/kol/vehicles/${vehicleId}`}
          aria-label={t('common.back', { defaultValue: 'חזרה' })}
          className="w-10 h-10 rounded-full border border-carnaf-line bg-carnaf-paper-elevated grid place-items-center text-carnaf-ink-muted shadow-kol-1"
        >
          <Icon name="arrow" size={19} />
        </Link>
      }
    >
      <InviteForm vehicleId={vehicleId} onSent={() => setRefresh((n) => n + 1)} />

      {error && (
        <p role="alert" className="text-carnaf-red mt-6">
          {error}
        </p>
      )}

      {grants === null && !error && <p className="text-carnaf-ink-muted mt-6">{t('common.loading')}</p>}

      {grants && grants.length === 0 && !error && (
        <p className="text-carnaf-ink-muted mt-6">{t('permissions.empty')}</p>
      )}

      {grants && grants.length > 0 && (
        <ul className="mt-6 space-y-3">
          {grants.map((g) => (
            <GrantCard key={g.id} grant={g} onRevoked={() => setRefresh((n) => n + 1)} />
          ))}
        </ul>
      )}
    </Layout>
  );
}

function InviteForm({ vehicleId, onSent }: { vehicleId: string; onSent: () => void }) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [scope, setScope] = useState<Exclude<PermissionScope, 'OWNER'>>('DRIVE');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentPhone, setSentPhone] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await sendInvite(vehicleId, { grantToPhone: phone, scope });
      setSentPhone(phone);
      setPhone('');
      onSent();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="rounded-2xl bg-white border border-carnaf-line p-5">
      <h2 className="text-base font-semibold text-carnaf-ink mb-3">{t('permissions.invite_title')}</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block">
          <span className="block text-sm font-medium text-carnaf-ink mb-1.5">{t('permissions.phone_label')}</span>
          <input
            type="tel"
            inputMode="tel"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+972 50 1234567"
            className="w-full rounded-xl border border-carnaf-line bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-carnaf-teal"
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-carnaf-ink mb-1.5">{t('permissions.scope_label')}</span>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as Exclude<PermissionScope, 'OWNER'>)}
            className="w-full rounded-xl border border-carnaf-line bg-white px-4 py-3"
          >
            {GRANTABLE_SCOPES.map((s) => (
              <option key={s} value={s}>
                {scopeLabel(s, t)}
              </option>
            ))}
          </select>
          <span className="block text-[15px] text-carnaf-ink-muted mt-1">{scopeDescription(scope, t)}</span>
        </label>

        {error && (
          <p role="alert" className="text-sm text-carnaf-red">
            {error}
          </p>
        )}
        {sentPhone && <p className="text-sm text-carnaf-teal">{t('permissions.sent', { phone: sentPhone })}</p>}

        <button
          type="submit"
          disabled={pending}
          className="w-full rounded-2xl bg-carnaf-teal text-white font-semibold py-3 disabled:opacity-50"
        >
          {pending ? t('common.loading') : t('permissions.send')}
        </button>
      </form>
    </section>
  );
}

function GrantCard({ grant, onRevoked }: { grant: GrantRow; onRevoked: () => void }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state: 'revoked' | 'expired' | 'accepted' | 'pending' = grant.revokedAt
    ? 'revoked'
    : grant.expiresAt && new Date(grant.expiresAt).getTime() < Date.now()
      ? 'expired'
      : grant.acceptedAt
        ? 'accepted'
        : 'pending';

  async function onRevoke() {
    setPending(true);
    setError(null);
    try {
      await revokeGrant(grant.id);
      onRevoked();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  const badge = (
    {
      revoked: 'bg-carnaf-line text-carnaf-ink-muted',
      expired: 'bg-carnaf-line text-carnaf-ink-muted',
      accepted: 'bg-carnaf-teal/15 text-carnaf-teal-dark',
      pending: 'bg-carnaf-amber-soft text-carnaf-amber-ink',
    } as const
  )[state];

  return (
    <li className="rounded-2xl bg-white border border-carnaf-line p-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium text-carnaf-ink">{grant.grantToPhone}</span>
        <span className={`text-[15px] px-2 py-1 rounded-full ${badge}`}>{t(`permissions.state.${state}`)}</span>
      </div>
      <p className="text-[15px] text-carnaf-ink-muted mt-1">
        {scopeLabel(grant.scope, t)} • {new Date(grant.createdAt).toLocaleDateString()}
      </p>
      {error && (
        <p role="alert" className="text-sm text-carnaf-red mt-2">
          {error}
        </p>
      )}
      {state !== 'revoked' && state !== 'expired' && (
        <button
          onClick={onRevoke}
          disabled={pending}
          className="mt-3 text-sm text-carnaf-red underline disabled:opacity-50"
        >
          {pending ? t('common.loading') : t('permissions.revoke')}
        </button>
      )}
    </li>
  );
}

function scopeLabel(scope: PermissionScope, t: (k: string) => string): string {
  return t(`permissions.scopes.${scope.toLowerCase()}`);
}
function scopeDescription(scope: Exclude<PermissionScope, 'OWNER'>, t: (k: string) => string): string {
  return t(`permissions.scope_desc.${scope.toLowerCase()}`);
}
